/**
 * 机上調査「解析の設定（新方式）」の入力欄 (docs/27 §15-3 / §15-6)。
 *
 * テンプレート (出力フォーマット編集画面) と地区の上書き (机上調査ページ) の両方で使う。空欄・「引き継ぐ」
 * を選んだ項目は、地区ではテンプレートに、テンプレートでは既定に従う。引き継ぐ値は各欄に表示する。
 */
import {
  DEFAULT_STREET_SURVEY_DESCRIPTION_INSTRUCTION,
  mergeStreetSurveyExtractionDef,
  resolveStreetSurveyExtraction,
  STREET_SURVEY_PLACEHOLDER_ANCHOR,
  STREET_SURVEY_PLACEHOLDER_EVALUATION,
  type AiOutputFormatSchema,
  type StreetSurveyExtractionDef,
} from '@citizen-walk/shared-types';
import {
  extractionDefFromDraft,
  type InheritableSwitch,
  type StreetSurveyExtractionDraft,
} from './streetSurveyExtractionDraft';

export interface StreetSurveyExtractionFormProps {
  /** 実効の出力フォーマット (項目の候補と既定値の解決に使う)。 */
  schema: AiOutputFormatSchema | null;
  draft: StreetSurveyExtractionDraft;
  onChange: (next: StreetSurveyExtractionDraft) => void;
  /** 引き継ぐ元の設定 (地区ではテンプレートの設定、テンプレートでは無し = 既定)。 */
  inheritedDef?: StreetSurveyExtractionDef | null;
  scope: 'district' | 'template';
  disabled?: boolean;
  /** 新方式を有効にできない理由 (旧方式の地区など)。あればチェックを無効にして表示する。 */
  enableBlockedReason?: string | null;
}

const inputClass = 'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

export function StreetSurveyExtractionForm({
  schema,
  draft,
  onChange,
  inheritedDef,
  scope,
  disabled = false,
  enableBlockedReason = null,
}: StreetSurveyExtractionFormProps) {
  const inheritWord = scope === 'district' ? 'テンプレートに従う' : '既定';
  // 引き継ぐ値 (空欄のときに使われる値) と、この下書きを重ねた結果。
  const inherited = resolveStreetSurveyExtraction(schema, {
    extraction: { ...(inheritedDef ?? {}), enabled: true },
  });
  const merged = resolveStreetSurveyExtraction(schema, {
    extraction: {
      ...mergeStreetSurveyExtractionDef(inheritedDef, extractionDefFromDraft(draft).def),
      enabled: true,
    },
  });
  if (!schema || merged.candidates.length === 0) {
    return (
      <p className="text-xs text-slate-400">
        マスタを参照する分類の項目を出力フォーマットに追加すると、新方式の設定ができます。
      </p>
    );
  }

  const set = (partial: Partial<StreetSurveyExtractionDraft>) => onChange({ ...draft, ...partial });
  const switchLabel = (value: InheritableSwitch, inheritedOn: boolean) =>
    value === 'inherit' ? `${inheritWord}（${inheritedOn ? '載せる' : '載せない'}）` : '';
  const anchorKey = merged.anchor?.key ?? '';

  return (
    <div className="grid grid-cols-1 gap-3">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 mt-0.5"
          checked={draft.enabled}
          disabled={disabled || !!enableBlockedReason}
          onChange={(e) => set({ enabled: e.target.checked })}
        />
        <span>
          {scope === 'district'
            ? 'この地区の机上調査を新方式で解析する'
            : 'このテンプレートを使う地区で、新方式を既定にする（地区ごとに変更できます）'}
          {enableBlockedReason && (
            <span className="block text-xs text-slate-500">{enableBlockedReason}</span>
          )}
        </span>
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs text-slate-600 min-w-0">
          1. 位置を検出する項目
          <select
            className={inputClass}
            style={{ maxWidth: '100%' }}
            value={draft.anchorAxisKey}
            disabled={disabled}
            onChange={(e) => set({ anchorAxisKey: e.target.value })}
          >
            <option value="">
              {inheritWord}（{inherited.anchor?.label ?? '該当なし'}）
            </option>
            {merged.candidates.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs text-slate-600 min-w-0">
          マスタの表記
          <select
            className={inputClass}
            style={{ maxWidth: '100%' }}
            value={draft.masterDetail}
            disabled={disabled}
            onChange={(e) =>
              set({
                masterDetail:
                  e.target.value === 'compact' || e.target.value === 'full' ? e.target.value : '',
              })
            }
          >
            <option value="">
              {inheritWord}（
              {inherited.masterDetail === 'compact' ? '値と名称だけ' : '判断基準・定義まで'}）
            </option>
            <option value="full">判断基準・定義まで載せる（精度重視）</option>
            <option value="compact">値と名称だけ（プロンプトを短くする）</option>
          </select>
        </label>
      </div>

      <fieldset className="text-xs text-slate-600">
        <legend>2. 位置を検出した各件に付ける項目</legend>
        <div className="mt-1 flex flex-wrap gap-3">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={draft.attachedMode === 'auto'}
              disabled={disabled}
              onChange={() => set({ attachedMode: 'auto' })}
            />
            {inheritWord}（
            {inherited.attached.length > 0
              ? inherited.attached.map((a) => a.label).join('、')
              : 'なし'}
            ）
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={draft.attachedMode === 'custom'}
              disabled={disabled}
              onChange={() => set({ attachedMode: 'custom' })}
            />
            選ぶ
          </label>
        </div>
        {draft.attachedMode === 'custom' && (
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {merged.candidates
              .filter((a) => a.key !== anchorKey)
              .map((a) => (
                <label key={a.key} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={draft.attachedAxisKeys.includes(a.key)}
                    onChange={(e) =>
                      set({
                        attachedAxisKeys: e.target.checked
                          ? [...draft.attachedAxisKeys, a.key]
                          : draft.attachedAxisKeys.filter((k) => k !== a.key),
                      })
                    }
                  />
                  {a.label}
                </label>
              ))}
          </div>
        )}
      </fieldset>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs text-slate-600 min-w-0">
          1 方向あたりの検出件数（1〜10）
          <input
            className={inputClass}
            inputMode="numeric"
            value={draft.perDirectionText}
            placeholder={`${inheritWord}: ${inherited.perDirectionMaxItems} 件`}
            disabled={disabled}
            onChange={(e) => set({ perDirectionText: e.target.value })}
          />
        </label>
        <label className="block text-xs text-slate-600 min-w-0">
          3. 地点全体の件数（1〜40）
          <input
            className={inputClass}
            inputMode="numeric"
            value={draft.totalText}
            placeholder={`${inheritWord}: ${inherited.totalMaxItems !== null ? `${inherited.totalMaxItems} 件` : '絞らない'}`}
            disabled={disabled}
            onChange={(e) => set({ totalText: e.target.value })}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs text-slate-600 min-w-0">
          4 方向をまとめるときの重複の扱い
          <select
            className={inputClass}
            style={{ maxWidth: '100%' }}
            value={draft.duplicateMode}
            disabled={disabled}
            onChange={(e) =>
              set({
                duplicateMode:
                  e.target.value === 'none' || e.target.value === 'same-object'
                    ? e.target.value
                    : '',
              })
            }
          >
            <option value="">
              {inheritWord}（
              {inherited.duplicateMode === 'none' ? '除かない' : '同じ物・同じ場所だけ除く'}）
            </option>
            <option value="same-object">同じ物・同じ場所だけを重複として除く</option>
            <option value="none">重複を除かない</option>
          </select>
        </label>
        <label className="block text-xs text-slate-600 min-w-0">
          マスタの利用ルール
          <select
            className={inputClass}
            style={{ maxWidth: '100%' }}
            value={draft.includeMasterUsageRules}
            disabled={disabled}
            onChange={(e) =>
              set({
                includeMasterUsageRules:
                  e.target.value === 'on' || e.target.value === 'off' ? e.target.value : 'inherit',
              })
            }
          >
            <option value="inherit">
              {switchLabel('inherit', inherited.includeMasterUsageRules)}
            </option>
            <option value="on">載せる</option>
            <option value="off">載せない</option>
          </select>
          <span className="mt-0.5 block text-[11px] text-slate-400">
            利用ルールには通常投稿向けの出力のしかたが書かれていることがあるため、既定では載せません。
          </span>
        </label>
      </div>

      <p className="text-[11px] text-slate-500">
        以下の文は、空欄なら{inheritWord}の文が使われます（薄い文字で表示）。
      </p>
      <label className="block text-xs text-slate-600">
        観察の指示（方向ごとの解析の冒頭に入る文）
        <textarea
          className={inputClass}
          rows={3}
          value={draft.observationInstruction}
          placeholder={inherited.observationInstruction}
          disabled={disabled}
          onChange={(e) => set({ observationInstruction: e.target.value })}
        />
      </label>
      <label className="block text-xs text-slate-600">
        良い点と課題の探し方（2. に評価の項目があるときだけ入る文）
        <textarea
          className={inputClass}
          rows={4}
          value={draft.evaluationGuidance}
          placeholder={inherited.evaluationGuidance}
          disabled={disabled}
          onChange={(e) => set({ evaluationGuidance: e.target.value })}
        />
        <span className="mt-0.5 block text-[11px] text-slate-400">
          {STREET_SURVEY_PLACEHOLDER_ANCHOR} は 1. の項目名、{STREET_SURVEY_PLACEHOLDER_EVALUATION}{' '}
          は評価の項目名に置き換わります。
        </span>
      </label>
      <label className="block text-xs text-slate-600">
        重複の判定（重複を「同じ物・同じ場所だけ除く」ときに入る文）
        <textarea
          className={inputClass}
          rows={3}
          value={draft.duplicateRule}
          placeholder={inherited.duplicateRule}
          disabled={disabled}
          onChange={(e) => set({ duplicateRule: e.target.value })}
        />
      </label>
      <label className="block text-xs text-slate-600">
        選抜の役割の文（4 方向をまとめて順位を付けるプロンプトの冒頭）
        <textarea
          className={inputClass}
          rows={1}
          value={draft.selectionRole}
          placeholder={inherited.selectionRole}
          disabled={disabled}
          onChange={(e) => set({ selectionRole: e.target.value })}
        />
      </label>
      <label className="block text-xs text-slate-600">
        地点の説明文の指示（4
        方向をまとめるときに書かせる説明文。文字数は出力フォーマットの説明文の長さが後ろに付く）
        <textarea
          className={inputClass}
          rows={2}
          value={draft.descriptionInstruction}
          placeholder={
            inherited.descriptionInstruction ?? DEFAULT_STREET_SURVEY_DESCRIPTION_INSTRUCTION
          }
          disabled={disabled}
          onChange={(e) => set({ descriptionInstruction: e.target.value })}
        />
      </label>
      <label className="block text-xs text-slate-600">
        エリアの要約の指示（エリア集計で AI に要約させるときの指示。地点の説明文の一覧の前に入る）
        <textarea
          className={inputClass}
          rows={2}
          value={draft.areaSummaryInstruction}
          placeholder={inherited.areaSummaryInstruction}
          disabled={disabled}
          onChange={(e) => set({ areaSummaryInstruction: e.target.value })}
        />
      </label>
      <label className="block text-xs text-slate-600">
        重要とみなす基準（4 方向をまとめて順位を付けるときの基準）
        <textarea
          className={inputClass}
          rows={2}
          value={draft.selectionCriteria}
          placeholder={inherited.selectionCriteria}
          disabled={disabled}
          onChange={(e) => set({ selectionCriteria: e.target.value })}
        />
      </label>
    </div>
  );
}
